import React from 'react';
import { Box, Divider, Typography } from '@mui/material';

import { InlinePart, MarkdownBlock, parseMarkdown } from './markdown';

/**
 * Zeigt die Antwort der KI an (#1336).
 *
 * Sprachmodelle antworten in Markdown — ohne Aufbereitung stehen `**` und
 * Aufzählungsstriche im Klartext in der Blase. Gebaut wird aus React-Elementen
 * und niemals aus eingesetztem HTML: was das Modell schickt, ist Text und
 * bleibt Text.
 */

const renderInline = (parts: InlinePart[], keyPrefix: string) =>
  parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;

    if (part.kind === 'bold') {
      return (
        <Box key={key} component='strong' sx={{ fontWeight: 600 }}>
          {part.text}
        </Box>
      );
    }

    if (part.kind === 'italic') {
      return (
        <Box key={key} component='em'>
          {part.text}
        </Box>
      );
    }

    if (part.kind === 'code') {
      return (
        <Box
          key={key}
          component='code'
          sx={{
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
        >
          {part.text}
        </Box>
      );
    }

    return <React.Fragment key={key}>{part.text}</React.Fragment>;
  });

const renderBlock = (block: MarkdownBlock, index: number) => {
  const key = `block-${index}`;

  if (block.kind === 'heading') {
    return (
      <Typography
        key={key}
        variant={block.level === 1 ? 'subtitle1' : 'subtitle2'}
        sx={{ fontWeight: 600, mt: index === 0 ? 0 : 1.5, mb: 0.5 }}
      >
        {renderInline(block.parts, key)}
      </Typography>
    );
  }

  if (block.kind === 'bullets' || block.kind === 'numbers') {
    return (
      <Box
        key={key}
        component={block.kind === 'numbers' ? 'ol' : 'ul'}
        sx={{ pl: 3, my: 0.5 }}
      >
        {block.items.map((item, itemIndex) => (
          <Typography
            key={`${key}-${itemIndex}`}
            component='li'
            variant='body2'
            sx={{ mb: 0.25 }}
          >
            {renderInline(item, `${key}-${itemIndex}`)}
          </Typography>
        ))}
      </Box>
    );
  }

  if (block.kind === 'code') {
    return (
      <Box
        key={key}
        component='pre'
        sx={{
          my: 1,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          // Lange Zeilen sollen im Fenster scrollen, nicht die Blase sprengen.
          overflowX: 'auto',
        }}
      >
        {block.text}
      </Box>
    );
  }

  if (block.kind === 'table') {
    return (
      // Eine schmale Blase und eine breite Tabelle vertragen sich nicht: die
      // Tabelle bekommt deshalb ihren eigenen waagerechten Lauf, statt die
      // Blase zu dehnen.
      <Box key={key} sx={{ my: 1, overflowX: 'auto' }}>
        <Box
          component='table'
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '0.8rem',
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1,
              py: 0.5,
              textAlign: 'left',
              verticalAlign: 'top',
            },
            '& th': { fontWeight: 600, bgcolor: 'action.hover' },
          }}
        >
          <Box component='thead'>
            <Box component='tr'>
              {block.header.map((cell, cellIndex) => (
                <Box component='th' key={`${key}-h-${cellIndex}`}>
                  {renderInline(cell, `${key}-h-${cellIndex}`)}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component='tbody'>
            {block.rows.map((row, rowIndex) => (
              <Box component='tr' key={`${key}-r-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <Box component='td' key={`${key}-r-${rowIndex}-${cellIndex}`}>
                    {renderInline(cell, `${key}-r-${rowIndex}-${cellIndex}`)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (block.kind === 'rule') {
    return <Divider key={key} sx={{ my: 1.5 }} />;
  }

  return (
    <Typography key={key} variant='body2' sx={{ mb: 0.75 }}>
      {renderInline(block.parts, key)}
    </Typography>
  );
};

export const KiMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ '& > *:last-child': { mb: 0 } }}>
    {parseMarkdown(text).map(renderBlock)}
  </Box>
);
